import React from "react";
import {
  InputAdornment,
  IconButton,
  Typography,
  useTheme as useMuiTheme,
  useMediaQuery,
  Grid,
} from "@mui/material";
import { Box } from "@mui/system";
import { Formik, Form, FormikTouched, FormikErrors } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { useTheme } from "styled-components";
import { AppFormikField } from "../../components/core/AppFormik/AppFormikField/AppFormikField";
import { useAutoFill } from "../../hooks/useAutoFill/useAutoFill";
import { Link, useNavigate } from "react-router-dom";
import { AppButton } from "../../components/core/AppButton/AppButton";
import { toast } from "react-toastify";
import { AppleIcon } from "../../assets/icons/AppleIcon";
import { GoogleIcon } from "../../assets/icons/GoogleIcon";
import { VisibilityOffIcon } from "../../assets/icons/VisibilityOffIcon";
import { VisibilityOnIcon } from "../../assets/icons/VisibilityOnIcon";
import { AppCheckBox } from "../../components/core/AppCheckBox/AppCheckBox";
import { getClientSelector } from "../../redux/client/clientSlice";
import { APP_TOASTIFY } from "../../styles/styles.constants";
import { getAuth, signInWithEmailAndPassword } from "firebase/auth";
import { _fs } from "../../services/firebase/config";
import { APP_ROUTES } from "../../constants/routes.constant";

const schema = Yup.object({
  email: Yup.string()
    .email("Invalid email address")
    .required("Required")
    .min(5, "Minimum length is 5")
    .max(255, "Maximum length is 255"),
  password: Yup.string()
    .min(8, "Must be 8 characters or more")
    .required("Required"),
});

const signIn = async (values: { email: string; password: string }) => {
  const auth = getAuth(_fs.app);
  const userCredential = await signInWithEmailAndPassword(
    auth,
    values.email,
    values.password
  );
  const user = userCredential.user;
  return user;
};

export const SignInPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAutoFill, autofillInputProps } = useAutoFill();
  const [showPassword, setShowPassword] = React.useState(false);
  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const isSmallScreen = useMediaQuery(useMuiTheme().breakpoints.down("lg"));
  const client = useSelector(getClientSelector);

  const {
    branding: { logoUrl, coverImgUrl },
  } = useSelector(getClientSelector);

  return (
    <Grid
      paddingY={0}
      paddingTop={isSmallScreen ? 14 : 0}
      paddingX={!isSmallScreen ? 0 : 1.25}
      sx={{
        backgroundColor: !isSmallScreen
          ? theme.common.white
          : theme.colorPrimary,
        boxSizing: "border-box",
        display: "flex",
        height: !isSmallScreen ? "100dvh" : "auto",
        overflow: !isSmallScreen ? "hidden" : "auto",
        width: "100%",
        justifyContent: "center",
      }}
    >
      {!isSmallScreen && (
        <Grid
          item
          display="flex"
          alignItems="center"
          md={6}
          sx={{
            width: "50%",
            height: "auto",
          }}
        >
          {!coverImgUrl ? (
            <Box
              display={"flex"}
              flexDirection={"column"}
              width={"100%"}
              height={"100%"}
              justifyContent={"center"}
              paddingX={5}
              sx={{
                background: theme.colorPrimary,
              }}
            >
              <Box
                display={"flex"}
                height={"100%"}
                justifyContent={"center"}
                flexDirection={"column"}
              >
                <Typography
                  mb={2.5}
                  fontWeight={400}
                  variant="h6"
                  color={theme.common.white}
                >
                  WELCOME TO
                </Typography>
                <Typography
                  mb={2.5}
                  fontWeight={700}
                  fontSize={"2rem"}
                  color={theme.common.white}
                >
                  {client.name} Football Academy
                </Typography>
              </Box>
              <Box mb={"87px"} width={"100%"} alignSelf={"end"}>
                <Typography
                  fontWeight={300}
                  color={theme.common.white}
                  variant="body2"
                >
                  Powered by YourZown
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box>
              <img
                alt="Club Cover"
                src={coverImgUrl}
                width={"100%"}
                height={"100%"}
              />
            </Box>
          )}
        </Grid>
      )}
      <Grid
        item
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent={isSmallScreen ? "start" : "center"}
        borderRadius={isSmallScreen ? "10px 10px 0px 0px" : "10px"}
        md={6}
        xs={12}
        sx={{
          background: !isSmallScreen
            ? theme.common.white
            : theme.colorSecondary,
        }}
        paddingX={0.625}
        paddingTop={isSmallScreen ? 5 : 1}
        paddingBottom={!isSmallScreen ? 1 : 0}
        width={isSmallScreen ? "440px" : "50%"}
        height={"auto"}
      >
        {isSmallScreen && (
          <>
            <Typography
              marginBottom={1.375}
              color={theme.common.white}
              fontWeight={"300"}
              variant="caption"
            >
              WELCOME!
            </Typography>
            <Typography
              color={theme.common.white}
              fontWeight={"600"}
              variant="h6"
              marginBottom={4.5}
            >
              Sign in
            </Typography>
          </>
        )}

        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent={isSmallScreen ? "flex-start" : "center"}
          paddingTop={isSmallScreen ? 3.75 : 0}
          borderRadius={isSmallScreen ? "10px 10px 0px 0px" : "10px"}
          width={1}
          sx={{
            backgroundColor: theme.common.white,
            height: "660px",
          }}
        >
          <Formik
            enableReinitialize={true}
            initialValues={{ email: "", password: "" }}
            validationSchema={schema}
            onSubmit={async (values: { email: string; password: string }) => {
              try {
                const user = await signIn(values);
                if (user) {
                  navigate(APP_ROUTES.home);
                }
              } catch (error) {
                toast("Invalid Credentials", APP_TOASTIFY.ERROR);
              }
            }}
          >
            {({
              errors,
              values,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
            }) => {
              const _touched: FormikTouched<{
                email: string;
                password: string;
              }> = touched;

              const _errors: FormikErrors<{ email: string; password: string }> =
                errors;

              return (
                <Form
                  style={{
                    width: isSmallScreen ? "100%" : 400,
                  }}
                >
                  <Box paddingX={2.5}>
                    <Box display="flex" justifyContent="center">
                      <img
                        width={"113px"}
                        height={"113px"}
                        src={logoUrl}
                        alt="Football Club Logo"
                      />
                    </Box>
                    {!isSmallScreen && (
                      <Typography
                        textAlign={"center"}
                        color={theme.common.black}
                        fontWeight={600}
                        variant="h6"
                        marginTop={1}
                        marginBottom={4.5}
                      >
                        Sign in
                      </Typography>
                    )}
                    <AppFormikField
                      name="email"
                      value={values.email}
                      label="EMAIL ADDRESS"
                      placeholder="Email Address"
                      InputLabelProps={{ shrink: isAutoFill || undefined }}
                      InputProps={autofillInputProps}
                      errorMessage={
                        _touched.email && _errors.email
                          ? String(errors.email)
                          : undefined
                      }
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type="text"
                      sx={{
                        marginTop: 3.375,
                      }}
                    />

                    <AppFormikField
                      name="password"
                      value={values.password}
                      label="PASSWORD"
                      placeholder="Password"
                      errorMessage={
                        _touched.password && _errors.password
                          ? String(errors.password)
                          : undefined
                      }
                      onBlur={handleBlur}
                      onChange={handleChange}
                      type={showPassword ? "text" : "password"}
                      InputLabelProps={{ shrink: isAutoFill || undefined }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={handleClickShowPassword}
                              edge="end"
                              sx={{
                                marginRight: "2px",
                              }}
                            >
                              {showPassword ? (
                                <VisibilityOffIcon />
                              ) : (
                                <VisibilityOnIcon />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        marginTop: 2,
                      }}
                    />
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent={"space-between"}
                      width="100%"
                      marginTop={1.25}
                      marginBottom={2}
                    >
                      <Box display="flex" alignItems="center">
                        <AppCheckBox
                          sx={{
                            width: 24,
                            height: 24,
                            marginRight: 1,
                          }}
                          size="medium"
                        />
                        <Typography
                          fontWeight={300}
                          color={theme.dark[400]}
                          variant="body2"
                        >
                          Stay signed in
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center">
                        <Link
                          style={{ color: theme.dark[700] }}
                          to={"/forgot-password"}
                        >
                          <Typography
                            color={theme.dark[700]}
                            fontWeight={400}
                            variant="body2"
                          >
                            Forgot password
                          </Typography>
                        </Link>
                      </Box>
                    </Box>
                    <AppButton isLoading={isSubmitting} type="submit">
                      <Typography
                        color={theme.common.white}
                        fontWeight={600}
                        variant="body1"
                      >
                        SIGN IN
                      </Typography>
                    </AppButton>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      my={2.75}
                    >
                      <Box
                        flexGrow={1}
                        borderBottom={1}
                        borderColor={theme.grey[600]}
                        mx={2}
                      />
                      <Typography
                        fontWeight={600}
                        color={theme.common.black}
                        variant="body2"
                      >
                        Or continue with
                      </Typography>
                      <Box
                        flexGrow={1}
                        borderBottom={1}
                        borderColor={theme.grey[600]}
                        mx={2}
                      />
                    </Box>
                    <Box
                      m={2}
                      display={"flex"}
                      gap={isSmallScreen ? 2.625 : 5}
                      justifyContent={"center"}
                    >
                      <IconButton
                        sx={{
                          borderRadius: "50%",
                          padding: 2,
                          boxShadow: "0px 5px 20px 0px #00000033",
                        }}
                      >
                        <GoogleIcon />
                      </IconButton>
                      <IconButton
                        sx={{
                          borderRadius: "50%",
                          padding: 2,
                          boxShadow: "0px 5px 20px 0px #00000033",
                        }}
                      >
                        <AppleIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </Form>
              );
            }}
          </Formik>
        </Box>
      </Grid>
    </Grid>
  );
};
